import type { ReactNode, ChangeEvent, FC } from 'react'
import { useRef } from 'react'
import { FieldInputProps, FormikHandlers, useField, useFormikContext } from 'formik'
import { Checkbox, CheckboxProps, FormControlLabel, FormControlLabelProps } from '@mui/material'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'

type Props = Pick<FormControlLabelProps, 'labelPlacement'> &
  Omit<CheckboxProps, keyof FieldInputProps<boolean>> & {
    name: string
    label?: string | ReactNode
    validateOnChange?: boolean
    onBlur?: FormikHandlers['handleBlur']
  }

export const CheckboxInput: FC<Props> = ({
  name,
  validateOnChange = false,
  label,
  onBlur,
  disabled,
  labelPlacement,
  ...props
}) => {
  const [field, , helpers] = useField<boolean>(name)
  const { isSubmitting, validateOnChange: formValidateOnChange } = useFormikContext()

  const inputRef = useRef<HTMLInputElement | null>(null)
  useFocusOnFirstError(field.name, inputRef, inputRef)

  const onChange = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    helpers.setValue(checked, formValidateOnChange || validateOnChange)
  }

  return (
    <FormControlLabel
      sx={!label ? { mr: 0, width: 'fit-content' } : {}}
      control={
        <Checkbox
          {...props}
          sx={{ m: 0, ...props.sx }}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          checked={!!field.value}
          inputRef={inputRef}
          disabled={isSubmitting || disabled}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
    />
  )
}
